import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from '../../constants/routes';
import { useApiErrorTranslation } from '../../hooks/useApiErrorTranslation';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useSuiteLoungeFlow } from '../../hooks/useSuiteLoungeFlow';
import { InputEmail } from '../InputEmail';

interface PasswordResetRequestFormData {
  email: string;
}

export const PasswordResetRequestForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { withEntry } = useSuiteLoungeFlow();
  const { translateApiError } = useApiErrorTranslation();
  const [errorMessage, setErrorMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetRequestFormData>();

  const { isRequesting, execute } = useApiRequest<'POST', 'PasswordResetEmail'>(
    'POST',
    'PasswordResetEmail',
  );

  const onFormSubmit = async (data: PasswordResetRequestFormData) => {
    setErrorMessage('');

    const result = await execute({ email: data.email });

    if (result.ok) {
      navigate(withEntry(ROUTE_PATH.PASSWORD_RESET_SENT));
    } else {
      const { message } = translateApiError(result.error);
      setErrorMessage(message);
    }
  };

  return (
    <>
      {/* ボックス1：パスワードの設定 */}
      <section className="l-card">
        <h2 className="l-card__title">{t('passwordReset.request.title')}</h2>
        <div className="l-text-group l-text-group--white">
          <p className="l-text">{t('passwordReset.request.description')}</p>
        </div>
        {/* 機能コンポーネント：フォーム */}
        <div className="c-auth c-auth--login">
          <form className="c-auth__form" onSubmit={handleSubmit(onFormSubmit)}>
            <div className="c-auth__fields">
              {/* メール */}
              <div className="c-auth__field">
                <label className="c-auth__label">
                  <span className="c-auth__label-text">
                    {t('passwordReset.request.email')}
                  </span>
                  <InputEmail
                    className="c-auth__input"
                    placeholder={t('passwordReset.request.emailPlaceholder')}
                    {...register('email', {
                      required: t(
                        'passwordReset.request.validation.emailRequired',
                      ),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t(
                          'passwordReset.request.validation.emailInvalid',
                        ),
                      },
                    })}
                  />
                </label>
                {errors.email && (
                  <div className="c-auth__alerts">
                    <p className="c-auth__alert c-auth__alert--error">
                      {errors.email.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="c-auth__actions">
              <button
                className="c-auth__btn c-auth__btn--login"
                type="submit"
                disabled={isRequesting}
              >
                {isRequesting
                  ? t('passwordReset.request.submitButtonLoading')
                  : t('passwordReset.request.submitButton')}
              </button>
            </div>
            {errorMessage && (
              <div className="c-auth__alerts">
                <p className="c-auth__alert c-auth__alert--error">
                  {errorMessage}
                </p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* ボックス2：アクション */}
      <section className="l-card l-mt30">
        <div className="l-card__body">
          <Link
            className="link-button link-button--signup-first"
            to={withEntry(ROUTE_PATH.LOGIN)}
          >
            {t('passwordReset.request.backToLogin')}
          </Link>
        </div>
      </section>
    </>
  );
};
